import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Dialog } from '@headlessui/react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { boxApi } from '../utils/api'
import { formatDateForDisplay, getLocalDateInputValue } from '../utils/date'

export default function Ledger({ boxId, canManage = true }) {
  const [entries, setEntries] = useState([])
  const [members, setMembers] = useState([])
  const [box, setBox] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [voidDialog, setVoidDialog] = useState({ open: false, entry: null })
  const [voidReason, setVoidReason] = useState('')
  const [voidSubmitting, setVoidSubmitting] = useState(false)
  const [voidError, setVoidError] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm()

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [ledgerData, membersData, boxData] = await Promise.all([
        boxApi.getLedger(boxId),
        boxApi.getMembers(boxId),
        boxApi.getById(boxId),
      ])
      setEntries(ledgerData)
      setMembers(membersData)
      setBox(boxData)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [boxId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const onSubmit = async (data) => {
    try {
      setSubmitting(true)
      await boxApi.createLedgerEntry(boxId, {
        ...data,
        amount: parseFloat(data.amount),
      })
      await loadData()
      setIsOpen(false)
      reset()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const openVoidDialog = (entry) => {
    setVoidDialog({ open: true, entry })
    setVoidReason('')
    setVoidError(null)
    setVoidSubmitting(false)
  }

  const closeVoidDialog = () => {
    setVoidDialog({ open: false, entry: null })
    setVoidReason('')
    setVoidError(null)
    setVoidSubmitting(false)
  }

  const handleVoidSubmit = async (event) => {
    event.preventDefault()
    const reason = voidReason.trim()
    if (!reason) {
      setVoidError('Reason is required')
      return
    }
    try {
      setVoidSubmitting(true)
      await boxApi.voidLedgerEntry(boxId, voidDialog.entry.id, { reason })
      await loadData()
      closeVoidDialog()
    } catch (err) {
      setVoidError(err.message)
    } finally {
      setVoidSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading ledger...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-800">Error loading ledger: {error}</p>
      </div>
    )
  }

  const getMemberById = (id) => members.find((m) => m.id === id)
  const expectedPayoutForMember = (memberId) => {
    const member = getMemberById(memberId)
    if (!member || !box) return null
    const contributionRaw = member.contribution_amount ?? box.contribution_amount ?? 0
    const contribution = Number(contributionRaw)
    const months = Number(box.cycle_duration_months) || 0
    if (Number.isNaN(contribution) || months <= 0) return null
    return contribution * months
  }
  const selectedMemberId = watch('member_id')
  const selectedMember = getMemberById(selectedMemberId)
  const selectedExpectedPayout = expectedPayoutForMember(selectedMemberId)
  const formatMoney = (value) => {
    const num = Number(value)
    if (Number.isNaN(num)) return '—'
    return `$${num.toFixed(2)}`
  }

  return (
    <div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Ledger</h3>
            <p className="mt-1 text-sm text-gray-500">
              Track all contributions and payouts. Payout expectations are based on member contribution amounts and the box cycle.
            </p>
          </div>
          {canManage && (
            <button
              onClick={() => setIsOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Add Entry
            </button>
          )}
        </div>

        {entries.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No ledger entries yet.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                {canManage && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => {
                const member = members.find((m) => m.id === entry.member_id)
                const isVoided = entry.status === 'void'
                return (
                  <tr key={entry.id} className={isVoided ? 'bg-red-50/40' : undefined}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateForDisplay(entry.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          entry.status === 'void'
                            ? 'bg-red-100 text-red-800'
                            : entry.type === 'contribution'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {entry.status === 'void' ? 'Voided' : entry.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${entry.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {isVoided ? (
                        <div>
                          <p className="text-red-600 font-medium">Voided</p>
                          <p className="text-xs text-red-500">{entry.void_reason || 'Reason not provided'}</p>
                        </div>
                      ) : (
                        entry.notes || '-'
                      )}
                    </td>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isVoided ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <button
                            onClick={() => openVoidDialog(entry)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Void entry
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Entry Modal */}
      <Dialog open={isOpen && canManage} onClose={() => setIsOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md rounded-3xl bg-white p-6 shadow-2xl border border-gray-100">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
              Add Ledger Entry
            </Dialog.Title>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Type *
                </label>
                <select
                  {...register('type', { required: 'Type is required' })}
                  className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Select type</option>
                  <option value="contribution">Contribution</option>
                  <option value="payout">Payout</option>
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Member *
                </label>
                <select
                  {...register('member_id', { required: 'Member is required' })}
                  className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Select member</option>
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
                {errors.member_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.member_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Amount *
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    {...register('amount', {
                      required: 'Amount is required',
                      min: { value: 0.01, message: 'Must be greater than 0' },
                    })}
                    className="block w-full pl-8 pr-4 rounded-2xl border border-gray-200 bg-white py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
                )}
                {selectedMember && (
                  <p className="mt-2 text-xs text-gray-500">
                    {selectedMember.name || selectedMember.email}: contributes{' '}
                    {formatMoney(selectedMember.contribution_amount ?? box?.contribution_amount ?? 0)} per month.
                    {selectedExpectedPayout !== null &&
                      ` Expected total payout this cycle: ${formatMoney(selectedExpectedPayout)} (prorated across their assigned slots).`}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Date *
                </label>
                <input
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                  defaultValue={getLocalDateInputValue()}
                  className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Notes
                </label>
                <textarea
                  rows={2}
                  {...register('notes')}
                  className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false)
                    reset()
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-full shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={voidDialog.open && canManage} onClose={closeVoidDialog} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md rounded-3xl bg-white p-6 shadow-2xl border border-gray-100">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">
              Void ledger entry
            </Dialog.Title>
            <p className="text-sm text-gray-500">
              Provide a reason for voiding this entry. The original record will remain visible for auditing.
            </p>
            {voidError && <p className="mt-3 text-sm text-red-600">{voidError}</p>}
            <form className="mt-4 space-y-4" onSubmit={handleVoidSubmit}>
              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeVoidDialog}
                  className="px-4 py-2 border border-gray-200 rounded-full shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={voidSubmitting}
                  className="px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {voidSubmitting ? 'Voiding…' : 'Void entry'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  )
}
